import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { dataGlossarySections } from "./sections.js";
import { FormTitle, LoadingSpinner } from "~/components/design-system/index.js";
import { Container } from "~/components/layout/styleWrappers.js";

function LegacyDataGlossary() {
  return (
    <>
      {dataGlossarySections.map((section) => (
        <Container key={section.heading}>
          <Typography variant="h6" gutterBottom>
            {section.heading}
          </Typography>

          <Typography variant="body1" sx={{ mb: 2, whiteSpace: "pre-line" }}>
            {section.intro}
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table
              size="small"
              sx={{
                "& td, & th": {
                  borderRight: "1px solid rgba(224, 224, 224, 1)",
                  verticalAlign: "top",
                  "&:last-child": { borderRight: "none" },
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Term</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Definition</TableCell>
                  {section.variant === "durations" ? (
                    <>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Calculation
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Cleaning Rules
                      </TableCell>
                    </>
                  ) : (
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Data Context
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {section.variant === "notes" ? "Notes" : "Recorded On"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {section.rows.map((row, index) => {
                  const columnCount = section.variant === "durations" ? 5 : 4;
                  return (
                    <TableRow key={index}>
                      <TableCell
                        colSpan={row.isGroupHeader ? columnCount : 1}
                        sx={{
                          ...(row.isGroupHeader && {
                            fontWeight: "bold",
                            backgroundColor: "#f5f5f5",
                          }),
                        }}
                      >
                        {row.term}
                      </TableCell>
                      {!row.isGroupHeader && (
                        <>
                          <TableCell sx={{ whiteSpace: "pre-line" }}>
                            {row.definition}
                          </TableCell>
                          {"calculation" in row ? (
                            <>
                              <TableCell sx={{ whiteSpace: "pre-line" }}>
                                {row.calculation}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "pre-line" }}>
                                {row.cleaningRules}
                              </TableCell>
                            </>
                          ) : (
                            <TableCell sx={{ whiteSpace: "pre-line" }}>
                              {row.dataContext}
                            </TableCell>
                          )}
                          <TableCell>
                            {"notes" in row ? row.notes : row.recordedOn}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      ))}
    </>
  );
}

export interface DataGlossaryPageInnerProps {
  dataGlossaryMarkdown: string | null;
  loading: boolean;
  onBackClick: () => void;
}

export function DataGlossaryPageInner({
  dataGlossaryMarkdown,
  loading,
  onBackClick,
}: DataGlossaryPageInnerProps) {
  if (loading) {
    return <LoadingSpinner />;
  }

  const hasCustomGlossary = Boolean(dataGlossaryMarkdown);

  return (
    <>
      <Box sx={{ px: 4, pt: 2, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBackClick}
        >
          Back to Dashboard
        </Button>
      </Box>

      <FormTitle
        titleText="Data Glossary"
        useReducedTopPadding
        hasTitleBottomMargin={false}
      />

      {hasCustomGlossary ? (
        <Container>
          <Box
            sx={{
              "& table": {
                width: "100%",
                borderCollapse: "collapse",
                border: "1px solid rgba(224, 224, 224, 1)",
              },
              "& th, & td": {
                border: "1px solid rgba(224, 224, 224, 1)",
                px: 2,
                py: 1,
                verticalAlign: "top",
              },
              "& th": {
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <Markdown remarkPlugins={[remarkGfm]}>
              {dataGlossaryMarkdown ?? ""}
            </Markdown>
          </Box>
        </Container>
      ) : (
        <LegacyDataGlossary />
      )}
    </>
  );
}
